// src/components/ui/liquid-glass.jsx — layered liquid-glass card (bordeaux frosted glass, no deps)
import React, { useId } from 'react';
import PropTypes from 'prop-types';

const BLUR_MAP = {
  sm: 'blur(6px)',
  md: 'blur(12px)',
  lg: 'blur(18px)',
  xl: 'blur(24px)'
};

// Expand #rgb / #rrggbb into an rgba() string at the given opacity
function hexToRgba(hex, opacity) {
  const clean = String(hex).replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function LiquidGlassCard({
  children,
  tint = '#68150A',
  tone = 'dark',
  baseOpacity = 0.75,
  glowIntensity = 0.5,
  shadowIntensity = 0.5,
  borderRadius = 16,
  blurIntensity = 'xl',
  edgeOpacity = 0.15,
  saturate = 1.5,
  className = '',
  contentClassName = '',
  withRefraction = false,
  refractionScale = 16,
  refractionFrequency = 0.011,
  ...props
}) {
  const blur = BLUR_MAP[blurIntensity] || blurIntensity;
  const radius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
  const isLightTone = tone === 'light';
  // Dark tone values below are frozen — light-only retune must not touch them.
  const edgeRgb = '255, 255, 255';
  const shadowRgb = '0, 0, 0';
  const shadowAlpha = (0.08 + shadowIntensity * 0.12).toFixed(2);
  const sheenAlpha = (0.03 + glowIntensity * 0.06).toFixed(3);
  const edgeAlpha = edgeOpacity.toFixed(2);
  // Light tone renders as clear glass: transparent fill, whisper of backdrop
  // blur (no saturate boost), hairline border + faintest shadow for separation.
  const borderStyle = isLightTone
    ? '1px solid rgba(122, 63, 32, 0.08)'
    : `1px solid rgba(${edgeRgb}, ${edgeAlpha})`;
  const boxShadowStyle = isLightTone
    ? '0 1px 8px rgba(122, 96, 78, 0.06)'
    : `0 2px 12px rgba(${shadowRgb}, ${shadowAlpha}), inset 0 1px 0 rgba(${edgeRgb}, ${edgeAlpha})`;
  const topLineAlpha = '0.4';
  // Refraction-layer gradients reuse the (now white) edge tone; keep them
  // fainter in light so they add sheen, never dark edges.
  const refrBgTopAlpha = '0.16';
  const refrBgBottomAlpha = '0.07';
  const refrTopAlpha = '0.35';
  const refrBottomAlpha = '0.18';
  // Unique SVG filter id per instance — no collisions when several pills/cards mount.
  const rawId = useId().replace(/[^a-zA-Z0-9]/g, '');
  const refractionId = `liquid-refraction-${rawId}`;
  // Moderate per-channel scales: green carries the base, red/blue offset ±5 for a
  // subtle chromatic fringe. Keeps mobile GPUs happy (small displaced region only).
  const scaleG = Math.max(4, Math.min(28, refractionScale));
  const scaleR = Math.max(4, Math.min(28, refractionScale - 5));
  const scaleB = Math.max(4, Math.min(28, refractionScale + 5));
  const freq = refractionFrequency;

  // Light tone: clear glass — blur only (prop-driven, no saturate boost),
  // no refraction, no sheen. Dark branch below is byte-identical behavior.
  const backdropValue = isLightTone ? blur : `${blur} saturate(${saturate})`;
  const showRefraction = withRefraction && !isLightTone;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        borderRadius: radius,
        backgroundColor: hexToRgba(tint, baseOpacity),
        backdropFilter: backdropValue,
        WebkitBackdropFilter: backdropValue,
        border: borderStyle,
        boxShadow: boxShadowStyle
      }}
      {...props}
    >
      {/* Hidden SVG defs: fractalNoise displacement map + per-channel
          refraction (R/G/B displacement) + channel isolation + screen
          merges + gentle soften. Static — no animation, so
          prefers-reduced-motion needs no extra handling. */}
      {showRefraction && (
        <svg aria-hidden="true" focusable="false" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          <defs>
            <filter
              id={refractionId}
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
              colorInterpolationFilters="sRGB"
            >
              <feTurbulence type="fractalNoise" baseFrequency={`${freq} ${freq * 1.4}`} numOctaves="2" seed="32" result="map" />
              <feGaussianBlur in="map" stdDeviation="1.1" result="softMap" />
              <feDisplacementMap in="SourceGraphic" in2="softMap" scale={scaleR} xChannelSelector="R" yChannelSelector="R" result="dispR" />
              <feDisplacementMap in="SourceGraphic" in2="softMap" scale={scaleG} xChannelSelector="G" yChannelSelector="G" result="dispG" />
              <feDisplacementMap in="SourceGraphic" in2="softMap" scale={scaleB} xChannelSelector="B" yChannelSelector="B" result="dispB" />
              <feColorMatrix in="dispR" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="chanR" />
              <feColorMatrix in="dispG" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="chanG" />
              <feColorMatrix in="dispB" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="chanB" />
              <feBlend in="chanR" in2="chanG" mode="screen" result="rg" />
              <feBlend in="rg" in2="chanB" mode="screen" result="rgb" />
              <feGaussianBlur in="rgb" stdDeviation="0.35" result="out" />
              <feComposite in="out" in2="SourceGraphic" operator="over" />
            </filter>
          </defs>
        </svg>
      )}
      {/* Refraction character layer — sits BEHIND content (content stays
          unfiltered above for legibility). Filters only this layer's own
          highlights/gradients, never the nav text. Scoped to this pill. */}
      {showRefraction && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ borderRadius: radius, filter: `url(#${refractionId})` }}
        >
          <div
            className="absolute inset-0"
            style={{
              borderRadius: radius,
              background: `linear-gradient(180deg, rgba(${edgeRgb}, ${refrBgTopAlpha}) 0%, transparent 22%, transparent 78%, rgba(${edgeRgb}, ${refrBgBottomAlpha}) 100%)`
            }}
          />
          <div
            className="absolute inset-x-6 top-0"
            style={{
              height: '2px',
              background: `linear-gradient(to right, transparent, rgba(${edgeRgb}, ${refrTopAlpha}), transparent)`
            }}
          />
          <div
            className="absolute inset-x-8 bottom-0"
            style={{
              height: '1px',
              background: `linear-gradient(to right, transparent, rgba(${edgeRgb}, ${refrBottomAlpha}), transparent)`
            }}
          />
        </div>
      )}
      {/* Specular top-edge highlight + faint diagonal sheen (dark glass only) */}
      {!isLightTone && (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ borderRadius: radius }}
      >
        <div
          className="absolute inset-x-0 top-0"
          style={{
            height: '1px',
            background: `linear-gradient(to right, transparent, rgba(${edgeRgb}, ${topLineAlpha}), transparent)`
          }}
        />
        <div
          className="absolute -left-1/4 w-1/2"
          style={{
            top: '-40%',
            bottom: '-40%',
            transform: 'rotate(12deg)',
            background: `linear-gradient(to right, transparent, rgba(${edgeRgb}, ${sheenAlpha}), transparent)`
          }}
        />
      </div>
      )}
      <div className={`relative z-10 ${contentClassName}`}>
        {children}
      </div>
    </div>
  );
}

LiquidGlassCard.displayName = 'LiquidGlassCard';

LiquidGlassCard.propTypes = {
  children: PropTypes.node,
  tint: PropTypes.string,
  tone: PropTypes.oneOf(['dark', 'light']),
  baseOpacity: PropTypes.number,
  glowIntensity: PropTypes.number,
  shadowIntensity: PropTypes.number,
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  blurIntensity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  edgeOpacity: PropTypes.number,
  saturate: PropTypes.number,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  withRefraction: PropTypes.bool,
  refractionScale: PropTypes.number,
  refractionFrequency: PropTypes.number
};
